import { useState } from 'react'
import { Button } from '@web/components/ui/Button'
import { useAuth, useLogout } from '@web/lib/auth'
import { isSoundEnabled, playCorrect, setSoundEnabled } from '@web/lib/sound'
import { detectSupport, disablePush, enablePush, permissionState } from '@web/lib/push'
import { useCourse, useCourses } from '@web/lib/catalog'
import { useDashboard } from '@web/lib/progress'
import { StatsGrid, WeakAreasCard } from '@web/features/dashboard/cards'

/**
 * The fuller picture: where she stands overall, unit by unit, and what to
 * revisit. The dashboard answers "what now?"; this answers "how am I doing?".
 */
export function ProgressPage() {
  const { user } = useAuth()
  const logout = useLogout()
  const { data: dashboard, isPending } = useDashboard()
  const { data: courses } = useCourses()
  const courseId = courses?.[0]?.id
  const { data: course } = useCourse(courseId)

  return (
    <div className="mx-auto w-full max-w-lg px-5 pt-[max(1.25rem,var(--safe-top))] pb-6">
      <h1 className="font-display text-3xl font-bold text-ink-900">Your progress</h1>
      <p className="mt-1 text-ink-500">
        {user?.displayName} · {dashboard?.dashboard.streak.longest ?? 0} day best streak
      </p>

      {isPending && (
        <div className="mt-6 space-y-4" aria-hidden="true">
          <div className="h-32 animate-pulse rounded-card bg-sand-100" />
          <div className="h-48 animate-pulse rounded-card bg-sand-100" />
        </div>
      )}

      {dashboard && (
        <div className="mt-6 space-y-4">
          <StatsGrid stats={dashboard.dashboard.stats} />

          {course && (
            <section className="rounded-card bg-white p-5 shadow-sm ring-1 ring-brand-900/5">
              <h2 className="font-display text-lg font-bold text-ink-900">Unit by unit</h2>
              <ul className="mt-3 space-y-3">
                {course.units.map((unit) => {
                  const pct =
                    unit.lessons.length === 0
                      ? 0
                      : Math.round((unit.lessonsCompleted / unit.lessons.length) * 100)
                  return (
                    <li key={unit.id}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="truncate font-medium text-ink-900">
                          <span aria-hidden="true">{unit.icon} </span>
                          {unit.title}
                        </span>
                        <span className="shrink-0 text-sm text-ink-500 tabular-nums">
                          {unit.lessonsCompleted}/{unit.lessons.length}
                        </span>
                      </div>
                      <div
                        className="mt-1.5 h-2 overflow-hidden rounded-full bg-sand-100"
                        role="progressbar"
                        aria-valuenow={pct}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`${unit.title} progress`}
                      >
                        <div
                          className="h-full rounded-full bg-brand-500 transition-[width] duration-500"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  )
                })}
              </ul>
            </section>
          )}

          <WeakAreasCard areas={dashboard.dashboard.weakAreas} />
        </div>
      )}

      <div className="mt-8 space-y-4 border-t border-sand-200 pt-5">
        <SoundToggle />
        <NotificationsToggle />
        <Button variant="secondary" onClick={() => logout.mutate()} loading={logout.isPending}>
          Sign out
        </Button>
      </div>
    </div>
  )
}

/**
 * Sound preference.
 *
 * Plays the "correct" tone when switched on, so she hears exactly what she is
 * agreeing to rather than having to go find a lesson to test it — and because
 * the toggle is a tap, which is the gesture the AudioContext needs anyway.
 */
function SoundToggle() {
  const [enabled, setEnabled] = useState(isSoundEnabled)

  function toggle() {
    const next = !enabled
    setEnabled(next)
    setSoundEnabled(next)
    if (next) playCorrect()
  }

  return (
    <div className="flex items-center justify-between rounded-card bg-white p-4 ring-1 ring-brand-900/5">
      <div>
        <p className="font-medium text-ink-900">Sound effects</p>
        <p className="text-sm text-ink-500">A short tone when you answer.</p>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        aria-label="Sound effects"
        onClick={toggle}
        className={`relative h-7 w-12 shrink-0 rounded-full transition-colors ${
          enabled ? 'bg-brand-600' : 'bg-sand-300'
        }`}
      >
        <span
          aria-hidden="true"
          className={`absolute top-1 size-5 rounded-full bg-white transition-transform ${
            enabled ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  )
}

/**
 * Daily reminder opt-in.
 *
 * Three states rather than a plain switch, because the failure modes are not
 * interchangeable and a dead toggle with no explanation is worse than no
 * toggle at all:
 *
 *  - not installed (iOS, in Safari's tab): push cannot work here at all, and
 *    the fix is "Add to Home Screen", so that is what it says.
 *  - denied: on iOS this is terminal. The permission cannot be re-requested;
 *    the app has to be deleted and re-installed. Saying so beats a button
 *    that silently does nothing every time it is tapped.
 *  - granted: a real toggle.
 *
 * `enable` runs straight off the click with no await before it, because iOS
 * discards the permission prompt if the call is not synchronous with the
 * gesture — and a mistimed prompt counts as a denial, which is unrecoverable.
 */
function NotificationsToggle() {
  const [support] = useState(() => detectSupport())
  const [permission, setPermission] = useState(() => permissionState())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!support.supported) {
    return (
      <div className="rounded-card bg-white p-4 ring-1 ring-brand-900/5">
        <p className="font-medium text-ink-900">Daily reminder</p>
        <p className="text-sm text-ink-500">
          {support.reason === 'not-installed'
            ? 'Add VIVA to your Home Screen first — iPhone only allows reminders for installed apps.'
            : 'This browser cannot show reminders.'}
        </p>
      </div>
    )
  }

  const enabled = permission === 'granted'

  async function toggle() {
    setError(null)
    setBusy(true)
    try {
      if (enabled) {
        await disablePush()
        setPermission(permissionState())
      } else {
        const ok = await enablePush()
        setPermission(permissionState())
        if (!ok) setError('Reminders were not turned on.')
      }
    } catch {
      setError('Could not change reminders. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="rounded-card bg-white p-4 ring-1 ring-brand-900/5">
      <div className="flex items-center justify-between">
        <div>
          <p className="font-medium text-ink-900">Daily reminder</p>
          <p className="text-sm text-ink-500">
            {permission === 'denied'
              ? 'Blocked. Delete and re-add VIVA to your Home Screen to allow it.'
              : 'One nudge a day to keep the streak.'}
          </p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={enabled}
          aria-label="Daily reminder"
          disabled={busy || permission === 'denied'}
          onClick={toggle}
          className={`relative h-7 w-12 shrink-0 rounded-full transition-colors disabled:opacity-40 ${
            enabled ? 'bg-brand-600' : 'bg-sand-300'
          }`}
        >
          <span
            aria-hidden="true"
            className={`absolute top-1 size-5 rounded-full bg-white transition-transform ${
              enabled ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
      </div>
      {error && <p className="mt-2 text-sm text-clay-600">{error}</p>}
    </div>
  )
}
